'use client';

import { useState, useContext, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';

import Base from '../../layouts/Base';
import HeadLine from '../../components/HeadLine/HeadLine';
import TabArea from '../../components/TabArea/TabArea';
import Section from '../../components/Section/Section';
import Grid from '../../components/Grid/Grid';
import Box from '../../components/Box/Box';
import RelatedCard from '../../components/RelatedCard/RelatedCard';
import SingleDataValueItem from '../../components/SingleDataValueItem';
import Button from '../../components/Button/Button';
import Map from '../../components/Map/Map';
import AccordionItem from '../../components/AccordionItem/AccordionItem';
import HybridActivityTab from './components/HybridActivityTab';

import StreamField from '../../components/StreamField';

import { CountryContext } from '../../utils/country-provider';

import { formatNumberWithSuffix } from '../../utils/helpers';

import { t } from '../../i18n';

import s from './WatchtowerCountryPage.module.scss';
import classNames from 'classnames';
import Bookmark from '../../components/Bookmark/Bookmark';

const OVERVIEW_SLUG = 'overview';
const INDUSTRY_SLUG = 'industry';
const ECONOMY_SLUG = 'economy';
const POLITICS_SLUG = 'politics';
const HOLDINGS_SLUG = 'companies';
const HYBRID_ACTIVITY_SLUG = 'hybrid-activity';

const PREVIEW_TAB_STORAGE_KEY = 'currentPreviewTab';

const stripDomain = (url) => {
    try {
        // Base URL is only used for relative URLs and never appears in the output;
        // we just need a valid base so the constructor doesn't throw during SSR.
        return new URL(url, 'http://localhost').pathname;
    } catch {
        return url;
    }
};

const normalizePath = (path) => {
    if (!path) return '';
    return path.endsWith('/') ? path : path + '/';
};

const getActiveTab = (currentRelativeUrl, pathname) => {
    const base = normalizePath(stripDomain(currentRelativeUrl));
    const current = normalizePath(pathname);

    if (current === base) return OVERVIEW_SLUG;
    if (current.startsWith(base + INDUSTRY_SLUG)) return INDUSTRY_SLUG;
    if (current.startsWith(base + ECONOMY_SLUG)) return ECONOMY_SLUG;
    if (current.startsWith(base + POLITICS_SLUG)) return POLITICS_SLUG;
    if (current.startsWith(base + HOLDINGS_SLUG)) return HOLDINGS_SLUG;
    if (current.startsWith(base + HYBRID_ACTIVITY_SLUG))
        return HYBRID_ACTIVITY_SLUG;

    return OVERVIEW_SLUG;
};

const WatchtowerCountryPage = (props) => {
    const {
        relativeUrl,
        overviewPoliticsSection,
        overviewSocietySection: overviewIndustrySection,
        overviewEconomySection,
        societyTopSection: industryTopSection,
        societySections: industrySections,
        economyTopSection,
        economySections,
        politicalTopSection,
        politicalSections,
        map,
        previewSettings,
        overviewRelated,
        bookmarkedPages,
        showHybridActivityTab,
        hybridActivity,
        hybridActivitySections,
    } = props;

    const isPreview = !!previewSettings;
    const pathname = usePathname();

    const countryMeta = useContext(CountryContext);
    const countryCode = countryMeta?.code;

    const [activeTab, setActiveTab] = useState(
        isPreview ? OVERVIEW_SLUG : getActiveTab(relativeUrl, pathname)
    );

    const handleTabChangeInPreview = (tabId) => {
        sessionStorage.setItem(PREVIEW_TAB_STORAGE_KEY, tabId);
        setActiveTab(tabId);
    };

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        if (!isPreview) {
            return;
        }

        const currentTab = sessionStorage.getItem(PREVIEW_TAB_STORAGE_KEY);
        const availableTab =
            currentTab === HYBRID_ACTIVITY_SLUG && !showHybridActivityTab
                ? OVERVIEW_SLUG
                : currentTab;

        if (availableTab) {
            sessionStorage.setItem(PREVIEW_TAB_STORAGE_KEY, availableTab);
            setActiveTab(availableTab);
        }
    }, [isPreview, showHybridActivityTab]);

    const tabs = useMemo(
        () => ({
            id: 'horizontal-menu',
            label: t('watchtowerCountryPage.tabMenuLabel'),
            items: [
                {
                    title: t('watchtowerCountryPage.titleOverview'),
                    url: !isPreview ? relativeUrl : null,
                    buttonId: OVERVIEW_SLUG,
                    isActive: activeTab === OVERVIEW_SLUG,
                },
                {
                    title: t('watchtowerCountryPage.titlePolitics'),
                    url: !isPreview ? `${relativeUrl}${POLITICS_SLUG}/` : null,
                    buttonId: POLITICS_SLUG,
                    isActive: activeTab === POLITICS_SLUG,
                },
                {
                    title: t('watchtowerCountryPage.titleEconomy'),
                    url: !isPreview ? `${relativeUrl}${ECONOMY_SLUG}/` : null,
                    buttonId: ECONOMY_SLUG,
                    isActive: activeTab === ECONOMY_SLUG,
                },
                {
                    title: t('watchtowerCountryPage.titleIndustry'),
                    url: !isPreview ? `${relativeUrl}${INDUSTRY_SLUG}/` : null,
                    buttonId: INDUSTRY_SLUG,
                    isActive: activeTab === INDUSTRY_SLUG,
                },
                {
                    title: t('watchtowerCountryPage.titleHoldings'),
                    url: !isPreview ? `${relativeUrl}companies/` : null,
                    buttonId: HOLDINGS_SLUG,
                    isActive: activeTab === HOLDINGS_SLUG,
                },
                ...(showHybridActivityTab
                    ? [
                          {
                              title: t(
                                  'watchtowerCountryPage.titleHybridActivity'
                              ),
                              url: !isPreview
                                  ? `${relativeUrl}${HYBRID_ACTIVITY_SLUG}/`
                                  : null,
                              buttonId: HYBRID_ACTIVITY_SLUG,
                              isActive: activeTab === HYBRID_ACTIVITY_SLUG,
                          },
                      ]
                    : []),
            ],
        }),
        [activeTab, showHybridActivityTab]
    );

    const sections = {
        [OVERVIEW_SLUG]: {
            topSection: {
                gridSize: 4,
                content: overviewPoliticsSection,
                button: {
                    title: t('watchtowerCountryPage.btnPolitics'),
                    url: `${relativeUrl}${POLITICS_SLUG}`,
                },
            },
            sections: [
                {
                    title: t('watchtowerCountryPage.titleEconomy'),
                    gridSize: 4,
                    content: overviewEconomySection,
                    button: {
                        title: t('watchtowerCountryPage.btnEconomy'),
                        url: `${relativeUrl}${ECONOMY_SLUG}`,
                    },
                },
                {
                    title: t('watchtowerCountryPage.titleIndustry'),
                    gridSize: 4,
                    content: overviewIndustrySection,
                    button: {
                        title: t('watchtowerCountryPage.btnIndustry'),
                        url: `${relativeUrl}${INDUSTRY_SLUG}`,
                    },
                },
            ],
            slug: OVERVIEW_SLUG,
        },
        [POLITICS_SLUG]: {
            topSection: politicalTopSection,
            sections: politicalSections,
        },
        [ECONOMY_SLUG]: {
            topSection: economyTopSection,
            sections: economySections,
        },
        [INDUSTRY_SLUG]: {
            topSection: industryTopSection,
            sections: industrySections,
        },
        [HOLDINGS_SLUG]: {
            topSection: null,
            sections: [],
            slug: HOLDINGS_SLUG,
        },
        // Absent when the switch is off. The route 404s in that case, so this
        // only covers previews and payloads that predate the switch.
        ...(showHybridActivityTab
            ? {
                  [HYBRID_ACTIVITY_SLUG]: {
                      topSection: null,
                      sections: hybridActivitySections || [],
                      slug: HYBRID_ACTIVITY_SLUG,
                  },
              }
            : {}),
    };

    const currentSection =
        activeTab in sections ? sections[activeTab] : sections[OVERVIEW_SLUG];

    useEffect(() => {
        if (isPreview) {
            return;
        }

        setActiveTab(getActiveTab(relativeUrl, pathname));
    }, [pathname]);

    const topSectionClasses = classNames(s.TopSection, {
        [s['TopSection--WithButton']]: currentSection?.topSection?.button,
    });

    const currentTabCapitalized = activeTab
        ? activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
        : '';

    const isMapSection =
        currentSection.slug === HOLDINGS_SLUG ||
        currentSection.slug === HYBRID_ACTIVITY_SLUG;

    // One renderer for a tab's chart sections, so the hybrid activity tab
    // shows an editor's sections exactly as the other tabs do.
    const renderChartSections = (
        chartSections,
        { noPaddingTop = false, isGreyOnDesktopOnly = false } = {}
    ) =>
        chartSections?.map(
            (
                { title, hideSectionForCountries, content, gridSize, button },
                index
            ) => {
                if (!content?.length) {
                    return null;
                }

                if (
                    countryCode &&
                    hideSectionForCountries?.includes(countryCode)
                ) {
                    return null;
                }

                // remove chart streamfields that are hidden for the current country
                const filteredStreamFieldContent = content.filter(
                    (c) =>
                        typeof c?.value?.hideChartForCountries ===
                            'undefined' ||
                        c.value.hideChartForCountries?.includes(countryCode) ===
                            false
                );
                const shouldRemoveTopPadding =
                    noPaddingTop && !title && !button;
                const shouldMatchSectionInset =
                    noPaddingTop && (title || button);

                return (
                    <Section
                        key={index}
                        modifier={isGreyOnDesktopOnly ? 'GreyDesktop' : 'Grey'}
                        noPaddingTop={shouldRemoveTopPadding}>
                        <div
                            className={classNames(s.SectionTitle, {
                                [s['SectionTitle--MatchSectionInset']]:
                                    shouldMatchSectionInset,
                            })}>
                            {title && <HeadLine title={title} size="Medium" />}
                            {button && (
                                <div className={s.Button}>
                                    <Button
                                        modifier="Secondary"
                                        size="Large"
                                        url={button.url}
                                        title={button.title}
                                    />
                                </div>
                            )}
                        </div>

                        <Grid columns={gridSize}>
                            {filteredStreamFieldContent && (
                                <StreamField
                                    smallPadding
                                    noWrapper={true}
                                    items={filteredStreamFieldContent}
                                />
                            )}
                        </Grid>
                    </Section>
                );
            }
        );

    const contentClasses = classNames(s.Content, {
        [s['Content--Map']]: isMapSection,
    });

    return (
        <div className={s.Root}>
            <div className={s.BookmarkMobile}>
                <Bookmark
                    url={pathname}
                    isSmall={true}
                    categories={['Watchtower', countryMeta?.name]}
                    alternativeTitle={currentTabCapitalized}
                />
            </div>

            <main className={s.Main}>
                <div className={s.TabArea}>
                    <TabArea
                        useNextRouter={true}
                        {...tabs}
                        isLink={!isPreview}
                        setActiveItem={
                            isPreview ? handleTabChangeInPreview : undefined
                        }
                        activeItem={isPreview ? activeTab : undefined}
                    />
                    <div className={s.BookmarkDesktop}>
                        <Bookmark
                            url={pathname}
                            isSmall={false}
                            categories={['Watchtower', countryMeta?.name]}
                            alternativeTitle={currentTabCapitalized}
                        />
                    </div>
                </div>

                <div className={contentClasses}>
                    {currentSection.slug === HOLDINGS_SLUG && (
                        <Section
                            noMarginBottom
                            isMainSection
                            modifier="NetworkTab">
                            <Map
                                {...map}
                                isSingleCountry
                                isHoldingsShown
                                bookmarkedPages={bookmarkedPages}
                                activeCountryName={countryMeta?.name}
                            />
                        </Section>
                    )}

                    {currentSection.slug === HYBRID_ACTIVITY_SLUG && (
                        <Section
                            noMarginBottom
                            isMainSection
                            modifier="NetworkTab">
                            {/* The tab owns the point fetch and the applied
                                date window, so the map and the incident log
                                below the charts read the same filtered
                                dataset. */}
                            <HybridActivityTab
                                countryCode={countryCode}
                                hybridActivity={hybridActivity}
                                // The page's path, not the browser's: in the
                                // CMS preview the browser is on /_draft. The
                                // serializer hands a full URL on multi-site
                                // setups, and the backend wants a path.
                                htmlPath={stripDomain(relativeUrl)}
                                // Configuration gaps are told to whoever can
                                // close them, and only in preview.
                                isPreview={isPreview}>
                                {renderChartSections(currentSection.sections, {
                                    noPaddingTop: true,
                                    isGreyOnDesktopOnly: true,
                                })}
                            </HybridActivityTab>
                        </Section>
                    )}

                    {!isMapSection && (
                        <Section
                            isScrollable
                            isMainSection
                            modifier="NetworkTab">
                            {currentSection?.topSection?.content?.length >
                                0 && (
                                <div className={topSectionClasses}>
                                    <div className={s.TopSectionHeader}>
                                        {currentSection?.topSection?.button && (
                                            <div className={s.ButtonMain}>
                                                <Button
                                                    modifier="Secondary"
                                                    size="Large"
                                                    url={
                                                        currentSection
                                                            .topSection.button
                                                            .url
                                                    }
                                                    title={
                                                        currentSection
                                                            .topSection.button
                                                            .title
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {currentSection.topSection && (
                                        <div className={s.SectionHeader}>
                                            {currentSection?.topSection && (
                                                <Grid
                                                    columns={
                                                        currentSection
                                                            .topSection.gridSize
                                                    }>
                                                    <StreamField
                                                        smallPadding
                                                        noWrapper={true}
                                                        items={currentSection.topSection.content.filter(
                                                            (
                                                                c // remove chart streamfields that are hidden for the current country
                                                            ) =>
                                                                typeof c?.value
                                                                    ?.hideChartForCountries ===
                                                                    'undefined' ||
                                                                c.value.hideChartForCountries?.includes(
                                                                    countryCode
                                                                ) === false
                                                        )}
                                                    />
                                                </Grid>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {renderChartSections(currentSection.sections)}

                            {currentSection.slug === OVERVIEW_SLUG && (
                                <Section modifier="Grey">
                                    <div
                                        className={classNames(
                                            s.SectionTitle,
                                            s['SectionTitle--WithPadding']
                                        )}>
                                        <HeadLine
                                            title={t(
                                                'watchtowerCountryPage.titleHoldings'
                                            )}
                                            size="Medium"
                                        />
                                        <div className={s.Button}>
                                            <Button
                                                modifier="Secondary"
                                                size="Large"
                                                url={`${relativeUrl}companies/`}
                                                title={t(
                                                    'watchtowerCountryPage.btnHoldings'
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <Grid
                                        columns={
                                            !!map?.overviewAccordion?.html
                                                ? 2
                                                : 3
                                        }
                                        modifier="WithGap">
                                        {!!map?.overviewAccordion?.html && (
                                            <div className={s.Accordion}>
                                                <AccordionItem
                                                    open={true}
                                                    title={'Overview'}
                                                    size="Medium"
                                                    content={
                                                        map?.overviewAccordion
                                                            ?.html
                                                    }
                                                />
                                            </div>
                                        )}
                                        {/* <Box
                                            title={t(
                                                'watchtowerCountryPage.titleTopCompanies'
                                            )}>
                                            <ImageTextList
                                                items={
                                                    map?.countryList
                                                        ?.countries?.[0]
                                                        ?.topCompanies
                                                }
                                            />
                                        </Box> */}
                                        {map?.countryList?.countries?.[0]?.data.map(
                                            (item, index) => {
                                                const formattedValue =
                                                    item.unit.toLowerCase() ===
                                                    'usd'
                                                        ? formatNumberWithSuffix(
                                                              item.value
                                                          )
                                                        : {
                                                              value: parseInt(
                                                                  item.value
                                                              ),
                                                              suffix: '',
                                                          };

                                                return (
                                                    <Box key={index}>
                                                        <SingleDataValueItem
                                                            title={item.title}
                                                            description={''}
                                                            value={
                                                                formattedValue.value
                                                            }
                                                            unit={`${formattedValue.suffix} ${item.unit}`}
                                                        />
                                                    </Box>
                                                );
                                            }
                                        )}
                                    </Grid>
                                </Section>
                            )}
                            {currentSection.slug === OVERVIEW_SLUG &&
                                overviewRelated &&
                                overviewRelated.news.length > 0 && (
                                    <Section modifier="Grey">
                                        <div
                                            className={classNames(
                                                s.SectionTitle,
                                                s['SectionTitle--WithPadding']
                                            )}>
                                            <HeadLine
                                                title={t(
                                                    'watchtowerCountryPage.relatedNews'
                                                )}
                                                size="Medium"
                                            />
                                            {/* <div className={s.Button}>
                                            <Button
                                                modifier="Secondary"
                                                size="Large"
                                                url={`${relativeUrl}companies/`}
                                                title={t(
                                                    'watchtowerCountryPage.btnRelatedReports'
                                                )}
                                            />
                                        </div> */}
                                        </div>
                                        <Grid columns={2} modifier="WithGap">
                                            {overviewRelated.news.map(
                                                (news, index) => (
                                                    <RelatedCard
                                                        tag={t(
                                                            'watchtowerCountryPage.news'
                                                        )}
                                                        modifiers={['Grey']}
                                                        key={index}
                                                        {...news}
                                                        // title={news.articleTitle}
                                                    />
                                                )
                                            )}
                                        </Grid>
                                    </Section>
                                )}
                            {currentSection.slug === OVERVIEW_SLUG &&
                                overviewRelated &&
                                overviewRelated.reports.length > 0 && (
                                    <Section modifier="Grey">
                                        <div
                                            className={classNames(
                                                s.SectionTitle,
                                                s['SectionTitle--WithPadding']
                                            )}>
                                            <HeadLine
                                                title={t(
                                                    'watchtowerCountryPage.relatedReports'
                                                )}
                                                size="Medium"
                                            />
                                            {/* <div className={s.Button}>
                                            <Button
                                                modifier="Secondary"
                                                size="Large"
                                                url={`${relativeUrl}companies/`}
                                                title={t(
                                                    'watchtowerCountryPage.btnRelatedReports'
                                                )}
                                            />
                                        </div> */}
                                        </div>
                                        <Grid columns={2} modifier="WithGap">
                                            {overviewRelated.reports.map(
                                                (report, index) => (
                                                    <RelatedCard
                                                        key={index}
                                                        {...report}
                                                        modifiers={['Grey']}
                                                        tag={t(
                                                            'watchtowerCountryPage.report'
                                                        )}
                                                        title={
                                                            report.articleTitle
                                                        }
                                                        isReport={true}
                                                    />
                                                )
                                            )}
                                        </Grid>
                                    </Section>
                                )}
                        </Section>
                    )}
                </div>
            </main>
        </div>
    );
};

WatchtowerCountryPage.propTypes = {};
export const RawWatchtowerCountryPage = WatchtowerCountryPage;
export default Base(WatchtowerCountryPage);
